import React, { useState, useEffect } from 'react';
import { Users, Plus, Mail, Phone, Building2, MapPin, Briefcase, RefreshCw, Cloud, CheckCircle, Trash2, Linkedin, Search, Link, Loader2, ExternalLink, GraduationCap, Globe, Edit, X, Pencil, Check } from 'lucide-react';
import { apiCall } from '../services/api';
import { toast } from 'sonner';

const RealEstateManagingPartnerContacts = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [icloudContacts, setIcloudContacts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncStatus, setSyncStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'a-z', 'z-a', 'newest' - default to newest
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContact, setEditedContact] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [linkedinImportedData, setLinkedinImportedData] = useState(null);
  const [linkedinUrlInput, setLinkedinUrlInput] = useState(''); // LinkedIn URL input field
  const [isSearchingLinkedIn, setIsSearchingLinkedIn] = useState(false); // For auto-search
  const [callingContactId, setCallingContactId] = useState(null); // Track which contact is being called
  
  // Nylas Connected Accounts State
  const [nylasAccounts, setNylasAccounts] = useState([]);
  const [nylasContacts, setNylasContacts] = useState([]);
  const [activeNylasAccount, setActiveNylasAccount] = useState('all'); // 'all', 'icloud', or specific email
  const [loadingNylasContacts, setLoadingNylasContacts] = useState(false);
  
  // iCloud contacts sync status (from existing icloud contacts in DB)
  const [icloudContactsCount, setIcloudContactsCount] = useState(0);
  const [icloudSynced, setIcloudSynced] = useState(false);
  
  // iCloud credentials modal state
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showGoogleContactsModal, setShowGoogleContactsModal] = useState(false);
  const [showOutlookContactsModal, setShowOutlookContactsModal] = useState(false);
  const [showAddContactAccountModal, setShowAddContactAccountModal] = useState(false);
  const [icloudEmail, setIcloudEmail] = useState('');
  const [icloudAppPassword, setIcloudAppPassword] = useState('');
  const [credentialError, setCredentialError] = useState('');
  
  // Contact Accounts Management State (similar to email accounts)
  const [contactAccounts, setContactAccounts] = useState(() => {
    try {
      const stored = localStorage.getItem('contact_accounts');
      if (stored) {
        return JSON.parse(stored);
      }
      // Default account - iCloud is connected with existing contacts
      return [
        { id: 'icloud_default', name: 'iCloud', type: 'icloud', email: '', connected: true, contactCount: 4287 }
      ];
    } catch { 
      return [
        { id: 'icloud_default', name: 'iCloud', type: 'icloud', email: '', connected: true, contactCount: 4287 }
      ];
    }
  });
  const [activeContactAccount, setActiveContactAccount] = useState('icloud_default');
  const [newContactAccountName, setNewContactAccountName] = useState('');
  const [newContactAccountEmail, setNewContactAccountEmail] = useState('');
  const [newContactAccountType, setNewContactAccountType] = useState('icloud');
  const [newContactAccountPassword, setNewContactAccountPassword] = useState('');
  const [addContactAccountError, setAddContactAccountError] = useState('');
  
  // Custom Contact Categories State
  const [customContactCategories, setCustomContactCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('contact_categories');
      return saved ? JSON.parse(saved) : null; // null means use defaults
    } catch { return null; }
  });
  const [showContactCategoryModal, setShowContactCategoryModal] = useState(false);
  const [newContactCategoryName, setNewContactCategoryName] = useState('');
  const [newContactCategoryIcon, setNewContactCategoryIcon] = useState('📁');
  const [editingContactCategory, setEditingContactCategory] = useState(null);
  const [editContactCategoryValue, setEditContactCategoryValue] = useState('');
  
  // Inline category edit state (for editing category directly on contact card)
  const [editingContactCardCategory, setEditingContactCardCategory] = useState(null); // contact id being edited
  
  // Available emoji icons for categories
  const categoryIcons = ['📋', '🔐', '🏢', '⚕️', '💻', '💰', '⚽', '⚖️', '🎓', '🔧', '🚗', '👨‍👩‍👧‍👦', '👥', '📁', '🎯', '🌟', '💼', '🎨', '🎵', '✈️', '🍽️', '🛒', '📧', '📱', '🏠', '💡', '🔒', '📊', '🎮', '🏋️'];
  
  // Save custom categories to localStorage
  
  // Save contact accounts to localStorage
  useEffect(() => {
    localStorage.setItem('contact_accounts', JSON.stringify(contactAccounts));
  }, [contactAccounts]);
  
  // Fetch Nylas connected accounts on mount
  useEffect(() => {
    const fetchNylasAccounts = async () => {
      try {
        // Use window.location.origin for same-origin requests (works for preview and production)
        const apiBase = window.location.origin;
        const response = await fetch(`${apiBase}/api/nylas/accounts?user_id=dwoodson`);
        if (response.ok) {
          const data = await response.json();
          if (data.accounts && data.accounts.length > 0) {
            setNylasAccounts(data.accounts);
            // If we have Nylas accounts, set the first one as active by default
            if (data.accounts.length > 0) {
              setActiveNylasAccount(data.accounts[0].email);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching Nylas accounts:', error);
      }
    };
    
    fetchNylasAccounts();
  }, []);
  
  // Fetch Nylas contacts when active account changes
  useEffect(() => {
    const fetchNylasContacts = async () => {
      if (nylasAccounts.length === 0) return;
      
      setLoadingNylasContacts(true);
      try {
        // Use window.location.origin for same-origin requests
        const apiBase = window.location.origin;
        
        // Fetch contacts from all accounts or a specific one
        const accountsToFetch = activeNylasAccount === 'all' 
          ? nylasAccounts 
          : nylasAccounts.filter(a => a.email === activeNylasAccount);
        
        const allContacts = [];
        
        for (const account of accountsToFetch) {
          try {
            // Use email parameter to look up grant_id on the backend
            const response = await fetch(
              `${apiBase}/api/nylas/contacts?user_id=dwoodson&email=${encodeURIComponent(account.email)}&limit=200`
            );
            if (response.ok) {
              const data = await response.json();
              if (data.contacts) {
                // Add account email to each contact for filtering
                const contactsWithAccount = data.contacts.map(c => ({
                  ...c,
                  account_email: account.email,
                  source: 'nylas'
                }));
                allContacts.push(...contactsWithAccount);
              }
            }
          } catch (err) {
            console.error(`Error fetching contacts for ${account.email}:`, err);
          }
        }
        
        setNylasContacts(allContacts);
      } catch (error) {
        console.error('Error fetching Nylas contacts:', error);
      } finally {
        setLoadingNylasContacts(false);
      }
    };
    
    fetchNylasContacts();
  }, [nylasAccounts, activeNylasAccount]);
  
  // Handle adding new contact account
  const handleAddContactAccount = () => {
    if (!newContactAccountName.trim()) {
      setAddContactAccountError('Please enter a name for this contact account');
      return;
    }
    if (newContactAccountType === 'icloud' && !newContactAccountPassword.trim()) {
      setAddContactAccountError('Please enter your app-specific password for iCloud');
      return;
    }
    
    const newAccount = {
      id: `${newContactAccountType}_${Date.now()}`,
      name: newContactAccountName.trim(),
      type: newContactAccountType,
      email: newContactAccountEmail.trim(),
      password: newContactAccountPassword.trim() || null,
      connected: newContactAccountType === 'icloud' && newContactAccountPassword.trim() ? true : false,
      contactCount: 0
    };
    
    // Store credentials for the account
    if (newContactAccountPassword.trim()) {
      localStorage.setItem(`contact_creds_${newAccount.id}`, JSON.stringify({
        email: newContactAccountEmail.trim(),
        password: newContactAccountPassword.trim()
      }));
    }
    
    setContactAccounts(prev => [...prev, newAccount]);
    setActiveContactAccount(newAccount.id);
    setShowAddContactAccountModal(false);
    setNewContactAccountName('');
    setNewContactAccountEmail('');
    setNewContactAccountType('icloud');
    setNewContactAccountPassword('');
    setAddContactAccountError('');
  };
  
  // Handle disconnecting a contact account
  const handleDisconnectContactAccount = (accountId) => {
    setContactAccounts(prev => prev.map(acc => 
      acc.id === accountId ? { ...acc, connected: false } : acc
    ));
    localStorage.removeItem(`contact_creds_${accountId}`);
  };
  
  // Handle removing a contact account
  const handleRemoveContactAccount = (accountId) => {
    setContactAccounts(prev => prev.filter(acc => acc.id !== accountId));
    localStorage.removeItem(`contact_creds_${accountId}`);
    if (activeContactAccount === accountId) {
      setActiveContactAccount(contactAccounts[0]?.id || 'icloud_default');
    }
  };
  
  const contactsPerPage = 50;
  // Use consistent user_id that matches stored contacts in database
  const userId = 'dwoodson';
  
  // Get API URL - use same origin to avoid CORS issues
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      // Always use same origin - works for localhost, preview, and production
      return window.location.origin;
    }
    return '';
  };
  
  const apiUrl = getApiUrl();
  
  // Function to initiate a Twilio call
  const initiateCall = async (contact, phoneNumber) => {
    if (!phoneNumber) {
      alert('No phone number available for this contact');
      return;
    }
    
    try {
      setCallingContactId(contact.icloud_id || contact.id);
      
      const response = await apiCall('/calls/make-call', {
        method: 'POST',
        body: JSON.stringify({
          to_phone: phoneNumber,
          contact_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown',
          contact_id: contact.icloud_id || contact.id
        })
      });
      
      if (response.call_sid) {
        alert(`Call initiated to ${contact.first_name || phoneNumber}. Check the Communications page for call status.`);
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      alert('Failed to initiate call. Please try again.');
    } finally {
      setCallingContactId(null);
    }
  };
  
  // Function to open email client
  const sendEmail = (email) => {
    if (!email) {
      alert('No email address available for this contact');
      return;
    }
    window.location.href = `mailto:${email}`;
  };
  
  // Function to open edit modal for a contact
  const openEditModal = (contact, e) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setEditedContact({
      ...contact,
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      organization: contact.organization || '',
      job_title: contact.job_title || '',
      emails: contact.emails || [],
      phones: contact.phones || [],
      notes: contact.notes || ''
    });
    setIsEditMode(true);
    setIsDetailModalOpen(true);
  };
  
  const openModal = (modalType) => {
    console.log('Opening modal:', modalType);
  };

  // Load cached contacts from localStorage and database on mount (NO AUTO-SYNC)
  useEffect(() => {
    const loadContacts = async () => {
      console.log('🔄 Loading contacts...');
      
      // First try localStorage for instant loading
      const cachedContacts = localStorage.getItem('icloud_contacts');
      const cachedLastSync = localStorage.getItem('icloud_last_sync');
      const cachedAuthStatus = localStorage.getItem('icloud_authenticated');
      
      if (cachedContacts) {
        try {
          const contacts = JSON.parse(cachedContacts);
          setIcloudContacts(contacts);
          setIcloudContactsCount(contacts.length);
          setIcloudSynced(contacts.length > 0);
          console.log(`✅ Loaded ${contacts.length} cached contacts from localStorage`);
        } catch (e) {
          console.error('❌ Failed to parse cached contacts:', e);
          localStorage.removeItem('icloud_contacts');
        }
      }
      
      if (cachedLastSync) {
        try {
          setLastSync(new Date(cachedLastSync));
        } catch (e) {
          console.error('Failed to parse last sync date');
        }
      }
      
      if (cachedAuthStatus === 'true') {
        setIsAuthenticated(true);
      }
      
      // Then try to load from database (more reliable than localStorage)
      try {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 15000;
        xhr.open('GET', `${apiUrl}/api/icloud-contacts/stored?user_id=${userId}`, true);
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data.contacts && data.contacts.length > 0) {
                setIcloudContacts(data.contacts);
                setIcloudContactsCount(data.contacts.length);
                setIcloudSynced(true);
                localStorage.setItem('icloud_contacts', JSON.stringify(data.contacts));
                console.log(`✅ Loaded ${data.contacts.length} contacts from database`);
              }
            } catch (e) {
              console.error('Failed to parse database contacts');
            }
          }
        };
        xhr.onerror = () => console.log('Could not load contacts from database');
        xhr.ontimeout = () => console.log('Database contacts request timed out');
        xhr.send();
      } catch (e) {
        console.log('Error loading from database:', e);
      }
    };
    
    loadContacts();
  }, [apiUrl, userId]);

  const authenticateAndSync = async (appleId = null, appPassword = null) => {
    // Get credentials from parameters, state, or localStorage
    const email = appleId || icloudEmail || localStorage.getItem('icloud_email');
    const password = appPassword || icloudAppPassword || localStorage.getItem('icloud_app_password');
    
    if (!email || !password) {
      setShowCredentialsModal(true);
      return;
    }
    
    setAuthenticating(true);
    setSyncStatus('🔄 Connecting to iCloud...');
    setCredentialError('');
    
    try {
      // Step 1: Authenticate - use XMLHttpRequest with timeout
      console.log('🔄 Authenticating with iCloud...');
      console.log('API URL:', apiUrl);
      
      const authData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 30000; // 30 second timeout for auth
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/authenticate?apple_id=${encodeURIComponent(email)}&app_password=${encodeURIComponent(password)}&user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else if (xhr.status === 502 || xhr.status === 504) {
            reject(new Error('Server timeout. Please try again.'));
          } else if (xhr.status === 401) {
            reject(new Error('Invalid credentials. Please check your Apple ID and App Password.'));
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Request timed out. Please try again.'));
        xhr.send();
      });
      
      console.log('✅ Authenticated:', authData.message);
      setIsAuthenticated(true);
      localStorage.setItem('icloud_authenticated', 'true');
      localStorage.setItem('icloud_email', email);
      localStorage.setItem('icloud_app_password', password);
      setShowCredentialsModal(false);
      
      // Step 2: Sync contacts - longer timeout for sync operation
      setSyncStatus('🔄 Syncing contacts (this may take a minute)...');
      
      const syncData = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.timeout = 120000; // 2 minute timeout for sync (iCloud can be slow)
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/quick-sync?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else if (xhr.status === 502 || xhr.status === 504) {
            reject(new Error('Server timeout during sync. Your contacts may still be syncing - please wait and refresh the page.'));
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Sync timed out. Your contacts may still be syncing in the background.'));
        xhr.send();
      });
      
      console.log(`✅ Synced ${syncData.synced_count} contacts`);
      
      // Save to state and localStorage
      setIcloudContacts(syncData.contacts || []);
      const syncTime = new Date(syncData.last_sync);
      setLastSync(syncTime);
      
      localStorage.setItem('icloud_contacts', JSON.stringify(syncData.contacts || []));
      localStorage.setItem('icloud_last_sync', syncTime.toISOString());
      
      setSyncStatus(`✅ ${syncData.synced_count} contacts synced`);
      
      setTimeout(() => setSyncStatus(''), 5000);
    } catch (error) {
      console.error('❌ Error:', error);
      const errorMsg = error.message || 'Unknown error';
      setSyncStatus(`❌ Sync failed: ${errorMsg}`);
      
      // If credential error, show modal again
      if (errorMsg.includes('Invalid credentials') || errorMsg.includes('401')) {
        setCredentialError('Invalid credentials. Please check your Apple ID and App Password.');
        setShowCredentialsModal(true);
        localStorage.removeItem('icloud_email');
        localStorage.removeItem('icloud_app_password');
        localStorage.removeItem('icloud_authenticated');
        setIsAuthenticated(false);
      }
      
      setTimeout(() => setSyncStatus(''), 5000);
    } finally {
      setAuthenticating(false);
      setSyncing(false);
    }
  };

  // Manual sync - ONLY called when button is clicked
  const handleSync = async () => {
    if (!isAuthenticated) {
      // Check if we have stored credentials
      const storedEmail = localStorage.getItem('icloud_email');
      const storedPassword = localStorage.getItem('icloud_app_password');
      
      if (storedEmail && storedPassword) {
        await authenticateAndSync(storedEmail, storedPassword);
      } else {
        setShowCredentialsModal(true);
      }
      return;
    }
    
    setSyncing(true);
    setSyncStatus('🔄 Syncing...');
    
    try {
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/api/icloud-contacts/quick-sync?user_id=${userId}`, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(xhr.responseText || `HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      
      // Update state and cache
      setIcloudContacts(data.contacts || []);
      const syncTime = new Date(data.last_sync);
      setLastSync(syncTime);
      
      localStorage.setItem('icloud_contacts', JSON.stringify(data.contacts || []));
      localStorage.setItem('icloud_last_sync', syncTime.toISOString());
      
      setSyncStatus(`✅ ${data.synced_count} contacts synced`);
      
      setTimeout(() => setSyncStatus(''), 3000);
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus(`❌ Sync failed: ${error.message}`);
      setTimeout(() => setSyncStatus(''), 3000);
    } finally {
      setSyncing(false);
    }
  };

  // Default category definitions
  const defaultCategories = [
    { id: 'all', name: 'All', icon: '📋', color: 'bg-gray-800', textColor: 'text-white' },
    { id: 'passwords', name: 'Passwords', icon: '🔐', color: 'bg-indigo-700', textColor: 'text-white' },
    { id: 'real-estate', name: 'Real Estate', icon: '🏢', color: 'bg-blue-600', textColor: 'text-white' },
    { id: 'healthcare', name: 'Healthcare', icon: '⚕️', color: 'bg-red-600', textColor: 'text-white' },
    { id: 'technology', name: 'Technology', icon: '💻', color: 'bg-purple-600', textColor: 'text-white' },
    { id: 'finance', name: 'Finance', icon: '💰', color: 'bg-green-600', textColor: 'text-white' },
    { id: 'sports', name: 'Sports', icon: '⚽', color: 'bg-orange-600', textColor: 'text-white' },
    { id: 'legal', name: 'Legal', icon: '⚖️', color: 'bg-yellow-600', textColor: 'text-black' },
    { id: 'education', name: 'Education', icon: '🎓', color: 'bg-indigo-600', textColor: 'text-white' },
    { id: 'services', name: 'Services', icon: '🔧', color: 'bg-teal-600', textColor: 'text-white' },
    { id: 'automotive', name: 'Automotive', icon: '🚗', color: 'bg-red-700', textColor: 'text-white' },
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', color: 'bg-pink-600', textColor: 'text-white' },
    { id: 'friends', name: 'Friends', icon: '👥', color: 'bg-cyan-600', textColor: 'text-white' },
    { id: 'other', name: 'Other', icon: '📁', color: 'bg-gray-600', textColor: 'text-white' }
  ];
  
  // Use custom categories if available, otherwise defaults
  const categories = customContactCategories || defaultCategories;
  
  // Category Management Functions
  const generateCategoryColor = (index) => {
    const colors = [
      { color: 'bg-blue-600', textColor: 'text-white' },
      { color: 'bg-purple-600', textColor: 'text-white' },
      { color: 'bg-pink-600', textColor: 'text-white' },
      { color: 'bg-red-600', textColor: 'text-white' },
      { color: 'bg-orange-600', textColor: 'text-white' },
      { color: 'bg-green-600', textColor: 'text-white' },
      { color: 'bg-teal-600', textColor: 'text-white' },
      { color: 'bg-cyan-600', textColor: 'text-white' },
      { color: 'bg-indigo-600', textColor: 'text-white' }
    ];
    return colors[index % colors.length];
  };
  
  const addContactCategory = () => {
    if (!newContactCategoryName.trim()) return;
    const categoryId = newContactCategoryName.toLowerCase().replace(/\s+/g, '-');
    
    // Check if already exists
    if (categories.some(c => c.id === categoryId)) {
      toast.error('Category already exists');
      return;
    }
    
    const colorConfig = generateCategoryColor(categories.length);
    const newCategory = {
      id: categoryId,
      name: newContactCategoryName,
      icon: newContactCategoryIcon,
      ...colorConfig
    };
    
    const updatedCategories = [...categories, newCategory];
    setCustomContactCategories(updatedCategories);
    localStorage.setItem('contact_categories', JSON.stringify(updatedCategories));
    setNewContactCategoryName('');
    setNewContactCategoryIcon('📁');
    toast.success(`Added "${newContactCategoryName}" category`);
  };
  
  const updateContactCategory = (oldId, newName, newIcon) => {
    if (!newName.trim()) return;
    const newId = newName.toLowerCase().replace(/\s+/g, '-');
    
    const updatedCategories = categories.map(c => {
      if (c.id === oldId) {
        return { ...c, id: newId, name: newName, icon: newIcon || c.icon };
      }
      return c;
    });
    
    setCustomContactCategories(updatedCategories);
    localStorage.setItem('contact_categories', JSON.stringify(updatedCategories));
    if (selectedCategory === oldId) setSelectedCategory(newId);
    setEditingContactCategory(null);
    setEditContactCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteContactCategory = (categoryId) => {
    // Protected categories
    const protectedCategories = ['all', 'passwords', 'real-estate', 'family', 'other'];
    if (protectedCategories.includes(categoryId)) {
      toast.error('Cannot delete default category');
      return;
    }
    
    const updatedCategories = categories.filter(c => c.id !== categoryId);
    setCustomContactCategories(updatedCategories);
    localStorage.setItem('contact_categories', JSON.stringify(updatedCategories));
    if (selectedCategory === categoryId) setSelectedCategory('all');
    toast.success('Category deleted');
  };

  // Update contact category inline (without opening full edit modal)
  const updateContactCategoryAssignment = async (contact, newCategoryId) => {
    const contactId = contact.id || contact.icloud_id;
    
    // Update the contact's category
    const updatedContact = { ...contact, category: newCategoryId };
    
    // Update in iCloud contacts if it's an iCloud contact
    if (contact.icloud_id) {
      const updatedContacts = icloudContacts.map(c => 
        c.icloud_id === contact.icloud_id ? updatedContact : c
      );
      setIcloudContacts(updatedContacts);
      localStorage.setItem('icloud_contacts', JSON.stringify(updatedContacts));
    }
    
    // Update in Nylas contacts if it's a Nylas contact
    if (contact.source === 'nylas') {
      const updatedNylasContacts = nylasContacts.map(c => 
        c.id === contact.id ? updatedContact : c
      );
      setNylasContacts(updatedNylasContacts);
    }
    
    // Update selected contact if it's the same one
    if (selectedContact && (selectedContact.id === contactId || selectedContact.icloud_id === contact.icloud_id)) {
      setSelectedContact(updatedContact);
    }
    
    // Close the inline editor
    setEditingContactCardCategory(null);
    
    const categoryName = categories.find(c => c.id === newCategoryId)?.name || newCategoryId;
    toast.success(`Category updated to "${categoryName}"`);
  };

  // Auto-categorize contacts based on their organization, emails, or notes
  const categorizeContact = (contact) => {
    const searchText = `${contact.organization || ''} ${contact.emails?.map(e => e.email).join(' ') || ''} ${contact.notes || ''} ${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
    
    // Passwords keywords (prioritize this - check first)
    if (searchText.includes('password') || searchText.includes('login') || searchText.includes('credential') || 
        searchText.includes('account') || searchText.includes('username') || searchText.includes('auth') ||
        searchText.includes('access') || searchText.includes('security') || searchText.includes('pin') ||
        searchText.includes('passcode') || searchText.includes('admin') || searchText.includes('1password') ||
        searchText.includes('lastpass') || searchText.includes('bitwarden') || searchText.includes('vault')) {
      return 'passwords';
    }
    
    // Real Estate keywords
    if (searchText.includes('realtor') || searchText.includes('real estate') || searchText.includes('realty') || 
        searchText.includes('broker') || searchText.includes('property') || searchText.includes('listing') || 
        searchText.includes('mortgage') || searchText.includes('escrow') || searchText.includes('title')) {
      return 'real-estate';
    }
    
    // Healthcare keywords
    if (searchText.includes('health') || searchText.includes('medical') || searchText.includes('doctor') || 
        searchText.includes('dr.') || searchText.includes('hospital') || searchText.includes('clinic') || 
        searchText.includes('physician') || searchText.includes('nurse') || searchText.includes('dental')) {
      return 'healthcare';
    }
    
    // Technology keywords
    if (searchText.includes('tech') || searchText.includes('software') || searchText.includes('it ') || 
        searchText.includes('developer') || searchText.includes('engineer') || searchText.includes('computer') ||
        searchText.includes('digital') || searchText.includes('cyber')) {
      return 'technology';
    }
    
    // Finance keywords
    if (searchText.includes('financ') || searchText.includes('bank') || searchText.includes('invest') || 
        searchText.includes('cpa') || searchText.includes('tax') || searchText.includes('account') ||
        searchText.includes('capital') || searchText.includes('fund') || searchText.includes('wealth')) {
      return 'finance';
    }
    
    // Sports keywords (prioritize this)
    if (searchText.includes('sport') || searchText.includes('athlete') || searchText.includes('coach') || 
        searchText.includes('team') || searchText.includes('nfl') || searchText.includes('nba') || 
        searchText.includes('patriots') || searchText.includes('cowboy') || searchText.includes('tiger') ||
        searchText.includes('football') || searchText.includes('basketball') || searchText.includes('baseball') ||
        searchText.includes('dlaw') || searchText.includes('lawrence')) {
      return 'sports';
    }
    
    // Legal keywords
    if (searchText.includes('law') || searchText.includes('attorney') || searchText.includes('legal') || 
        searchText.includes('lawyer') || searchText.includes('counsel') || searchText.includes('esq')) {
      return 'legal';
    }
    
    // Education keywords
    if (searchText.includes('school') || searchText.includes('university') || searchText.includes('education') || 
        searchText.includes('teacher') || searchText.includes('professor') || searchText.includes('tutor')) {
      return 'education';
    }
    
    // Services keywords
    if (searchText.includes('service') || searchText.includes('consultant') || searchText.includes('advisor') ||
        searchText.includes('shrg') || searchText.includes('consulting')) {
      return 'services';
    }
    
    // Automotive keywords
    if (searchText.includes('auto') || searchText.includes('car') || searchText.includes('vehicle') || 
        searchText.includes('dealership') || searchText.includes('automotive')) {
      return 'automotive';
    }
    
    // Family keywords (high priority)
    if (searchText.includes('wife') || searchText.includes('husband') || searchText.includes('son') || 
        searchText.includes('daughter') || searchText.includes('mom') || searchText.includes('dad') ||
        searchText.includes('brother') || searchText.includes('sister') || searchText.includes('family') ||
        searchText.includes('pats wife') || searchText.includes('spouse')) {
      return 'family';
    }
    
    // Friends - personal contacts without organization
    if (!contact.organization || contact.organization.trim() === '') {
      return 'friends';
    }
    
    return 'other';
  };

  // No mock contacts - only iCloud contacts
  const contacts = [];

  // Remove duplicates based on email, phone, or exact name match
  const removeDuplicates = (contacts) => {
    const seen = new Map();
    const unique = [];
    
    contacts.forEach(contact => {
      // Create unique keys for duplicate detection
      const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim().toLowerCase();
      const email = contact.emails?.[0]?.email?.toLowerCase() || '';
      const phone = contact.phones?.[0]?.number?.replace(/\D/g, '') || ''; // Remove non-digits
      
      // Create a composite key
      const key = `${name}|${email}|${phone}`;
      
      // Check if we've seen this contact before
      if (!seen.has(key)) {
        seen.set(key, true);
        unique.push(contact);
      } else {
        console.log(`🔄 Removed duplicate contact: ${name}`);
      }
    });
    
    return unique;
  };

  // Auto-categorize, remove duplicates, and sort contacts
  const categorizedContacts = removeDuplicates(icloudContacts)
    .map(contact => ({
      ...contact,
      category: categorizeContact(contact)
    }))
    .filter(contact => {
      // Filter out contacts with "Unknown" name
      const displayName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      return displayName && displayName.toLowerCase() !== 'unknown';
    })
    .sort((a, b) => {
      const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim().toLowerCase();
      const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
      
      if (sortOrder === 'a-z') {
        return nameA.localeCompare(nameB);
      } else if (sortOrder === 'z-a') {
        return nameB.localeCompare(nameA);
      } else if (sortOrder === 'newest') {
        // Sort by created_at timestamp descending (newest first)
        // Falls back to synced_at if created_at not available
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 
                      (a.synced_at ? new Date(a.synced_at).getTime() : 0);
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 
                      (b.synced_at ? new Date(b.synced_at).getTime() : 0);
        return dateB - dateA;
      }
      return 0;
    });

  // Combine and filter contacts - handle Nylas, iCloud, and 'all' tabs
  const getDisplayContacts = () => {
    // Helper function to filter by category
    const filterByCategory = (contacts) => {
      if (selectedCategory === 'all') return contacts;
      return contacts.filter(c => c.category === selectedCategory);
    };
    
    // Helper function to filter by search
    const filterBySearch = (contacts) => {
      if (!searchQuery) return contacts;
      const searchLower = searchQuery.toLowerCase();
      return contacts.filter(c => {
        const name = `${c.given_name || c.first_name || ''} ${c.surname || c.last_name || ''}`.toLowerCase();
        const email = (c.emails?.[0]?.email || c.email || '').toLowerCase();
        const org = (c.company_name || c.organization || '').toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower) || org.includes(searchLower);
      });
    };
    
    // Handle 'icloud' tab specifically
    if (activeNylasAccount === 'icloud') {
      let contactsToShow = categorizedContacts;
      contactsToShow = filterByCategory(contactsToShow);
      contactsToShow = filterBySearch(contactsToShow);
      return contactsToShow;
    }
    
    // Handle 'all' tab - combine iCloud and Nylas contacts
    if (activeNylasAccount === 'all') {
      let allContacts = [];
      
      // Add iCloud contacts
      if (icloudSynced) {
        allContacts = [...categorizedContacts.map(c => ({ ...c, source: 'icloud' }))];
      }
      
      // Add Nylas contacts
      if (nylasAccounts.length > 0) {
        allContacts = [...allContacts, ...nylasContacts];
      }
      
      // Apply category filter
      allContacts = filterByCategory(allContacts);
      
      // Apply search filter
      allContacts = filterBySearch(allContacts);
      
      return allContacts;
    }
    
    // Handle specific Nylas account tab
    if (nylasAccounts.length > 0) {
      let contactsToShow = nylasContacts;
      
      // Filter by active account
      contactsToShow = nylasContacts.filter(c => c.account_email === activeNylasAccount);
      
      // Apply category filter
      contactsToShow = filterByCategory(contactsToShow);
      
      // Apply search filter
      contactsToShow = filterBySearch(contactsToShow);
      
      return contactsToShow;
    }
    
    // Fall back to iCloud/legacy contacts
    let contactsToShow = categorizedContacts;
    contactsToShow = filterByCategory(contactsToShow);
    contactsToShow = filterBySearch(contactsToShow);
    return contactsToShow;
  };

  // Filter contacts based on selected category and search query
  const filteredContacts = (nylasAccounts.length > 0 || icloudSynced)
    ? getDisplayContacts()
    : categorizedContacts.filter(c => {
        const matchesCategory = selectedCategory === 'all' || c.category === selectedCategory;
        
        if (!searchQuery) return matchesCategory;
        
        const searchLower = searchQuery.toLowerCase();
        const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        const email = c.emails?.[0]?.email?.toLowerCase() || '';
        const org = c.organization?.toLowerCase() || '';
        
        return matchesCategory && (name.includes(searchLower) || email.includes(searchLower) || org.includes(searchLower));
      });

  // Pagination
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
  const startIndex = (currentPage - 1) * contactsPerPage;
  const endIndex = startIndex + contactsPerPage;
  const paginatedContacts = filteredContacts.slice(startIndex, endIndex);

  // Reset to page 1 when category or search changes
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleEditContact = () => {
    setIsEditMode(true);
    setEditedContact({ ...selectedContact });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedContact(null);
  };

  const handleSaveContact = async () => {
    // Check if this is a new contact or an existing one
    if (!selectedContact) {
      // This is a new contact - generate an ID and add to the list
      const newContact = {
        ...editedContact,
        icloud_id: `local-${Date.now()}`,
        id: `local-${Date.now()}`,
        created_at: new Date().toISOString(),
        source: 'manual'
      };
      
      const updatedContacts = [...icloudContacts, newContact];
      setIcloudContacts(updatedContacts);
      
      // Update in localStorage
      localStorage.setItem('icloud_contacts', JSON.stringify(updatedContacts));
      
      // Also save to backend
      try {
        await fetch(`${apiUrl}/api/contacts/icloud/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newContact)
        });
      } catch (error) {
        console.log('Note: Could not save to backend, saved locally');
      }
      
      // Close modal
      setIsDetailModalOpen(false);
      setIsEditMode(false);
      setEditedContact(null);
      
      console.log('✅ New contact added successfully');
      return;
    }
    
    // Update existing contact in state
    const updatedContacts = icloudContacts.map(contact => 
      contact.icloud_id === editedContact.icloud_id ? editedContact : contact
    );
    
    setIcloudContacts(updatedContacts);
    
    // Update in localStorage
    localStorage.setItem('icloud_contacts', JSON.stringify(updatedContacts));
    
    // Update selected contact
    setSelectedContact(editedContact);
    setIsEditMode(false);
    setEditedContact(null);
    
    console.log('✅ Contact updated successfully');
  };

  const handleDeleteContact = () => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete ${selectedContact.first_name || ''} ${selectedContact.last_name || ''}?`)) {
      return;
    }
    
    // Remove contact from state
    const updatedContacts = icloudContacts.filter(contact => 
      contact.icloud_id !== selectedContact.icloud_id
    );
    
    setIcloudContacts(updatedContacts);
    
    // Update in localStorage
    localStorage.setItem('icloud_contacts', JSON.stringify(updatedContacts));
    
    // Close modal
    setIsDetailModalOpen(false);
    setSelectedContact(null);
    setIsEditMode(false);
    setEditedContact(null);
    
    console.log('✅ Contact deleted successfully');
  };
  
  const handleLinkedInScan = async () => {
    if (!selectedContact) return;
    
    setIsScanning(true);
    setLinkedinImportedData(null); // Clear previous import data
    console.log('🔍 Starting LinkedIn scan for:', selectedContact.first_name, selectedContact.last_name);
    
    try {
      const searchName = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
      const searchCompany = selectedContact.organization || '';
      
      // Call LinkedIn scraper API
      const response = await fetch(`${apiUrl}/api/linkedin/scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: searchName,
          company: searchCompany
        })
      });
      
      if (!response.ok) {
        throw new Error('LinkedIn scan failed');
      }
      
      const result = await response.json();
      console.log('✅ LinkedIn API response:', result);
      
      // Extract the data from the response wrapper
      const data = result.data || result;
      const rawData = data.raw_data || {};
      
      // Check if we got any useful data - check both data and raw_data
      const hasData = data.name || data.job_title || data.company || data.bio || data.location || 
                      rawData.fullName || rawData.headline || rawData.companyName ||
                      (data.skills && data.skills.length > 0) || (data.experience && data.experience.length > 0);
      
      if (!hasData) {
        // Check for error in raw_data
        const errorMsg = rawData.error || 'No LinkedIn profile found for this person. Try entering their LinkedIn URL manually.';
        setLinkedinImportedData({ 
          error: true, 
          message: errorMsg,
          searchedName: searchName,
          searchedCompany: searchCompany
        });
        return;
      }
      
      // Parse the name - try multiple sources
      const fullName = data.name || rawData.fullName || '';
      let firstName = rawData.firstName || fullName.split(' ')[0] || '';
      let lastName = rawData.lastName || fullName.split(' ').slice(1).join(' ') || '';
      
      // Extract company from multiple sources
      const company = data.company || rawData.companyName || '';
      
      // Extract location from multiple sources
      const location = data.location || rawData.addressCountryOnly || rawData.jobLocation || '';
      
      // Extract job title from multiple sources
      const jobTitle = data.job_title || rawData.headline || rawData.jobTitle || '';
      
      // Extract company website
      const companyWebsite = rawData.companyWebsite || rawData.companyUrl || 
                            (rawData.websites?.length > 0 ? rawData.websites[0] : '');
      
      // Extract education - format nicely
      const educationArray = data.education || rawData.education || [];
      const educationText = Array.isArray(educationArray) ? 
        educationArray.map(edu => 
          `${edu.schoolName || edu.school || edu.name || ''}${edu.degree || edu.degreeName ? ` - ${edu.degree || edu.degreeName}` : ''}`
        ).filter(Boolean).join('; ') : '';
      
      // Store the imported data to display in UI
      const importedFields = {};
      if (firstName && firstName !== selectedContact.first_name) importedFields.first_name = firstName;
      if (lastName && lastName !== selectedContact.last_name) importedFields.last_name = lastName;
      if (company && company !== selectedContact.organization) importedFields.organization = company;
      if (jobTitle && jobTitle !== selectedContact.job_title) importedFields.job_title = jobTitle;
      if (location && location !== selectedContact.location) importedFields.location = location;
      if (rawData.linkedinUrl) importedFields.linkedin_url = rawData.linkedinUrl;
      if (data.profile_picture) importedFields.profile_image = data.profile_picture;
      if (data.bio) importedFields.about = data.bio;
      if (data.skills && data.skills.length > 0) importedFields.skills = data.skills;
      if (data.experience && data.experience.length > 0) importedFields.experience = data.experience;
      if (educationText) importedFields.education = educationText;
      if (data.email || rawData.email) importedFields.email = data.email || rawData.email;
      if (companyWebsite) importedFields.company_website = companyWebsite;
      
      console.log('📋 Imported fields:', importedFields);
      
      setLinkedinImportedData({
        raw: data,
        fields: importedFields,
        fieldCount: Object.keys(importedFields).length
      });
      
      const updatedContact = {
        ...(selectedContact || {}),
        first_name: firstName || selectedContact?.first_name || '',
        last_name: lastName || selectedContact?.last_name || '',
        organization: company || selectedContact?.organization || '',
        job_title: jobTitle || selectedContact?.job_title || '',
        location: location || selectedContact?.location || '',
        linkedin_url: rawData.linkedinUrl || selectedContact?.linkedin_url || '',
        profile_image: data.profile_picture || selectedContact?.profile_image || '',
        about: data.bio || selectedContact?.about || '',
        skills: data.skills || selectedContact?.skills || [],
        experience: data.experience || selectedContact?.experience || [],
        education: educationText || selectedContact?.education || '',
        company_website: companyWebsite || selectedContact?.company_website || '',
        // Preserve existing contact fields
        emails: selectedContact?.emails || [],
        phones: selectedContact?.phones || [],
        notes: selectedContact?.notes || '',
      };
      
      console.log('📝 Updated contact:', updatedContact);
      
      // IMPORTANT: Set editedContact FIRST, then set edit mode
      setEditedContact(updatedContact);
      setSelectedContact(updatedContact);
      // Update the LinkedIn URL input field
      if (rawData.linkedinUrl) {
        setLinkedinUrlInput(rawData.linkedinUrl);
      }
      
      // Now enable edit mode - the form will have data to display
      if (!isEditMode) {
        setIsEditMode(true);
      }
      
    } catch (error) {
      console.error('❌ LinkedIn scan error:', error);
      setLinkedinImportedData({ error: true, message: 'Failed to scan LinkedIn. Please try again or enter details manually.' });
    } finally {
      setIsScanning(false);
    }
  };

  // Scan a specific LinkedIn URL
  const handleLinkedInUrlScan = async (url) => {
    if (!url || !url.includes('linkedin.com')) {
      setLinkedinImportedData({ error: true, message: 'Please enter a valid LinkedIn URL (e.g., https://linkedin.com/in/username)' });
      return;
    }
    
    setIsScanning(true);
    setLinkedinImportedData(null);
    console.log('🔍 Scanning LinkedIn URL:', url);
    
    try {
      const response = await fetch(`${apiUrl}/api/linkedin/scrape/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile_url: url })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to scan LinkedIn profile');
      }
      
      const result = await response.json();
      console.log('✅ LinkedIn profile data:', result);
      
      const data = result.data || result;
      const rawData = data.raw_data || {};
      
      // Even if profile is private, we can still save the URL and any basic public data
      const hasAnyData = data.name || data.job_title || data.profile_picture || data.location || 
                         rawData.fullName || rawData.headline || rawData.companyName;
      
      // Parse the name - try multiple sources
      const fullName = data.name || rawData.fullName || '';
      let firstName = rawData.firstName || fullName.split(' ')[0] || '';
      let lastName = rawData.lastName || fullName.split(' ').slice(1).join(' ') || '';
      
      // Extract company from multiple sources
      const company = data.company || rawData.companyName || '';
      
      // Extract location from multiple sources
      const location = data.location || rawData.addressCountryOnly || rawData.jobLocation || '';
      
      // Extract job title from multiple sources
      const jobTitle = data.job_title || rawData.headline || rawData.jobTitle || '';
      
      // Extract company website
      const companyWebsite = rawData.companyWebsite || rawData.companyUrl || 
                            (rawData.websites?.length > 0 ? rawData.websites[0] : '');
      
      // Extract education - format nicely
      const educationArray = data.education || rawData.education || [];
      const educationText = Array.isArray(educationArray) ? 
        educationArray.map(edu => 
          `${edu.schoolName || edu.school || edu.name || ''}${edu.degree || edu.degreeName ? ` - ${edu.degree || edu.degreeName}` : ''}`
        ).filter(Boolean).join('; ') : '';
      
      // Store imported fields
      const importedFields = {};
      if (firstName) importedFields.first_name = firstName;
      if (lastName) importedFields.last_name = lastName;
      if (company) importedFields.organization = company;
      if (jobTitle) importedFields.job_title = jobTitle;
      if (location) importedFields.location = location;
      importedFields.linkedin_url = url; // Always save the URL
      if (data.profile_picture) importedFields.profile_image = data.profile_picture;
      if (data.bio) importedFields.about = data.bio;
      if (data.skills?.length > 0) importedFields.skills = data.skills;
      if (data.experience?.length > 0) importedFields.experience = data.experience;
      if (educationText) importedFields.education = educationText;
      if (data.email || rawData.email) importedFields.email = data.email || rawData.email;
      if (companyWebsite) importedFields.company_website = companyWebsite;
      
      // If we only got the URL (profile is very private), still count it as success
      if (!hasAnyData) {
        importedFields.linkedin_url = url;
      }
      
      console.log('📋 Imported fields:', importedFields);
      
      setLinkedinImportedData({
        raw: data,
        fields: importedFields,
        fieldCount: Object.keys(importedFields).length,
        partialData: !fullName && !jobTitle
      });
      
      // Build updated contact with LinkedIn data
      const updatedContact = {
        ...(selectedContact || {}),
        first_name: firstName || selectedContact?.first_name || '',
        last_name: lastName || selectedContact?.last_name || '',
        organization: company || selectedContact?.organization || '',
        job_title: jobTitle || selectedContact?.job_title || '',
        location: location || selectedContact?.location || '',
        linkedin_url: url,
        profile_image: data.profile_picture || selectedContact?.profile_image || '',
        about: data.bio || selectedContact?.about || '',
        skills: data.skills || selectedContact?.skills || [],
        experience: data.experience || selectedContact?.experience || [],
        education: educationText || selectedContact?.education || '',
        company_website: companyWebsite || selectedContact?.company_website || '',
        // Preserve existing contact fields
        emails: selectedContact?.emails || [],
        phones: selectedContact?.phones || [],
        notes: selectedContact?.notes || '',
      };
      
      console.log('📝 Updated contact:', updatedContact);
      
      // Set editedContact FIRST, then set edit mode
      setEditedContact(updatedContact);
      setSelectedContact(updatedContact);
      setLinkedinUrlInput(url);
      
      // Now enable edit mode - the form will have data to display
      if (!isEditMode) setIsEditMode(true);
      
    } catch (error) {
      console.error('❌ LinkedIn URL scan error:', error);
      setLinkedinImportedData({ error: true, message: error.message || 'Failed to scan LinkedIn profile.' });
    } finally {
      setIsScanning(false);
    }
  };

  // Auto-search for LinkedIn profile based on contact info
  const handleAutoSearchLinkedIn = async () => {
    if (!selectedContact) return;
    
    setIsSearchingLinkedIn(true);
    setLinkedinImportedData(null);
    
    const searchName = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
    const searchCompany = selectedContact.organization || '';
    const searchEmail = selectedContact.emails?.[0]?.email || '';
    
    console.log('🔍 Auto-searching LinkedIn for:', { searchName, searchCompany, searchEmail });
    
    try {
      // Use the existing name-based search endpoint
      const response = await fetch(`${apiUrl}/api/linkedin/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: searchName, company: searchCompany })
      });
      
      if (!response.ok) {
        throw new Error('Search failed');
      }
      
      const result = await response.json();
      const data = result.data || result;
      
      // Check if we found a URL
      const foundUrl = data.raw_data?.linkedinUrl;
      
      if (foundUrl) {
        setLinkedinUrlInput(foundUrl);
        setLinkedinImportedData({
          found: true,
          message: `Found potential LinkedIn profile! Click "Scan URL" to fetch the full profile.`,
          url: foundUrl
        });
      } else {
        setLinkedinImportedData({
          error: true,
          message: 'No LinkedIn profile found. Try entering the URL manually.',
          searchedName: searchName,
          searchedCompany: searchCompany
        });
      }
      
    } catch (error) {
      console.error('❌ Auto-search error:', error);
      setLinkedinImportedData({ error: true, message: 'Auto-search failed. Please enter the LinkedIn URL manually.' });
    } finally {
      setIsSearchingLinkedIn(false);
    }
  };

  const updateEditedField = (field, value) => {
    setEditedContact({ ...editedContact, [field]: value });
  };

  const updateEditedEmail = (index, value) => {
    const newEmails = [...(editedContact.emails || [])];
    newEmails[index] = { ...newEmails[index], email: value };
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const updateEditedPhone = (index, value) => {
    const newPhones = [...(editedContact.phones || [])];
    newPhones[index] = { ...newPhones[index], number: value };
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  const addEmail = () => {
    const newEmails = [...(editedContact.emails || []), { email: '', type: 'personal' }];
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const addPhone = () => {
    const newPhones = [...(editedContact.phones || []), { number: '', type: 'mobile' }];
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  const removeEmail = (index) => {
    const newEmails = editedContact.emails.filter((_, i) => i !== index);
    setEditedContact({ ...editedContact, emails: newEmails });
  };

  const removePhone = (index) => {
    const newPhones = editedContact.phones.filter((_, i) => i !== index);
    setEditedContact({ ...editedContact, phones: newPhones });
  };

  return (
    <div className="min-h-screen p-3 md:p-8 flex flex-col overflow-hidden bg-black">
      {/* Connected Contacts Tabs - Show when Nylas accounts OR iCloud synced */}
      {(nylasAccounts.length > 0 || icloudSynced) && (
        <div className="mb-4 bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-xl p-4" data-testid="contacts-accounts-panel">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="text-purple-400" size={20} />
                <h2 className="text-base font-bold text-white">Connected Contacts</h2>
                <span className="text-xs text-gray-400">via Nylas & iCloud</span>
              </div>
              <a 
                href="/settings/accounts"
                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
              >
                Manage Accounts
                <ExternalLink size={12} />
              </a>
            </div>
            
            {/* Contact Account Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="contacts-account-tabs">
              {/* All Contacts Tab */}
              <button
                onClick={() => setActiveNylasAccount('all')}
                className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  activeNylasAccount === 'all'
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                }`}
                data-testid="contacts-tab-all"
              >
                <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                All Contacts
                <span className={`text-[10px] md:text-xs ${activeNylasAccount === 'all' ? 'text-white/80' : 'text-gray-500'}`}>
                  ({nylasContacts.length + icloudContactsCount})
                </span>
              </button>
              
              {/* iCloud Tab - Show when iCloud contacts are synced */}
              {icloudSynced && (
                <button
                  onClick={() => setActiveNylasAccount('icloud')}
                  className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    activeNylasAccount === 'icloud'
                      ? 'bg-blue-600 text-white border-2 border-blue-400'
                      : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                  }`}
                  data-testid="contacts-tab-icloud"
                  title="iCloud Contacts"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span>iCloud</span>
                  <span className={`text-[10px] md:text-xs ${activeNylasAccount === 'icloud' ? 'text-white/80' : 'text-gray-500'}`}>
                    ({icloudContactsCount})
                  </span>
                </button>
              )}
              
              {/* Individual Nylas Account Tabs */}
              {nylasAccounts.map((account) => {
                const accountContactCount = nylasContacts.filter(c => c.account_email === account.email).length;
                const isActive = activeNylasAccount === account.email;
                
                return (
                  <button
                    key={account.email}
                    onClick={() => setActiveNylasAccount(account.email)}
                    className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                      isActive
                        ? 'bg-purple-600 text-white border-2 border-purple-400'
                        : 'bg-gray-800 border border-gray-600 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid={`contacts-tab-${account.email}`}
                    title={account.email}
                  >
                    {/* Provider Icon */}
                    {account.provider === 'google' ? (
                      <svg className="w-4 h-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    ) : account.provider === 'microsoft' || account.provider === 'outlook' ? (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0078D4">
                        <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                      </svg>
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center text-[10px] text-white font-bold">
                        {account.email.charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="max-w-[120px] md:max-w-[180px] truncate">
                      {account.account_name || account.email.split('@')[0]}
                    </span>
                    <span className={`text-[10px] md:text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                      ({accountContactCount})
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Loading/Status Indicator */}
            {loadingNylasContacts ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <Loader2 size={14} className="animate-spin text-purple-400" />
                <span className="text-xs text-purple-400">Loading contacts...</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <CheckCircle size={14} className="text-green-400" />
                <span className="text-xs text-green-400">
                  {activeNylasAccount === 'all' 
                    ? `${nylasContacts.length + icloudContactsCount} contacts from ${nylasAccounts.length + (icloudSynced ? 1 : 0)} account(s)`
                    : activeNylasAccount === 'icloud'
                      ? `${icloudContactsCount} contacts from iCloud`
                      : `${nylasContacts.filter(c => c.account_email === activeNylasAccount).length} contacts from ${activeNylasAccount}`
                  }
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legacy Contact Accounts Panel - Show only if no Nylas accounts AND no iCloud synced */}
      {nylasAccounts.length === 0 && !icloudSynced && (
      <div className="mb-4 bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-500/30 rounded-xl p-4">
        <div className="flex flex-col gap-3">
          {/* Header with Add Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="text-blue-400" size={20} />
              <h2 className="text-base font-bold text-white">Contact Accounts</h2>
            </div>
            <div className="flex items-center gap-2">
              <a 
                href="/settings/accounts"
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all text-xs"
                data-testid="connect-via-nylas-btn"
              >
                <Link size={14} />
                Connect via Nylas
              </a>
              <button 
                onClick={() => setShowAddContactAccountModal(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-all text-xs"
                data-testid="add-contact-account-btn"
              >
                <Plus size={14} />
                Add Account
              </button>
            </div>
          </div>
          
          {/* Account Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {contactAccounts.map((account) => (
              <div key={account.id} className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setActiveContactAccount(account.id)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                    activeContactAccount === account.id
                      ? account.type === 'icloud' 
                        ? 'bg-blue-600 text-white' 
                        : account.type === 'google'
                          ? 'bg-white text-black'
                          : 'bg-[#0078D4] text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  {account.type === 'icloud' && (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  )}
                  {account.type === 'google' && (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    </svg>
                  )}
                  {account.type === 'outlook' && (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229z"/>
                    </svg>
                  )}
                  {account.name}
                  {account.connected && (
                    <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected"></span>
                  )}
                  {account.contactCount > 0 && (
                    <span className="text-[10px] opacity-70">({account.contactCount})</span>
                  )}
                </button>
                
                {/* Actions for active account */}
                {activeContactAccount === account.id && (
                  <>
                    {account.connected ? (
                      <button
                        onClick={() => handleDisconnectContactAccount(account.id)}
                        className="p-1.5 rounded-lg bg-red-600/20 hover:bg-red-600/30 text-red-400 transition-all"
                        title="Disconnect"
                      >
                        <X size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (account.type === 'icloud') setShowCredentialsModal(true);
                          else if (account.type === 'google') setShowGoogleContactsModal(true);
                          else if (account.type === 'outlook') setShowOutlookContactsModal(true);
                        }}
                        className="p-1.5 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-400 transition-all"
                        title="Connect"
                      >
                        <Link size={14} />
                      </button>
                    )}
                    {/* Remove button for non-default accounts */}
                    {account.id !== 'icloud_default' && (
                      <button
                        onClick={() => handleRemoveContactAccount(account.id)}
                        className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-400 transition-all"
                        title="Remove account"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Connection Status for Active Account */}
          {contactAccounts.find(acc => acc.id === activeContactAccount)?.connected && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-green-400 font-medium">
                {contactAccounts.find(acc => acc.id === activeContactAccount)?.name} Connected
              </span>
              <span className="text-xs text-gray-400">
                • {contactAccounts.find(acc => acc.id === activeContactAccount)?.contactCount || filteredContacts.length} contacts synced
              </span>
              {lastSync && (
                <span className="text-xs text-gray-500 ml-auto">
                  Last sync: {lastSync.toLocaleString()}
                </span>
              )}
            </div>
          )}
          
          {/* Not Connected Status */}
          {!contactAccounts.find(acc => acc.id === activeContactAccount)?.connected && (
            <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <span className="w-2 h-2 bg-yellow-500 rounded-full" />
              <span className="text-xs text-yellow-400 font-medium">
                {contactAccounts.find(acc => acc.id === activeContactAccount)?.name} Not Connected
              </span>
              <span className="text-xs text-gray-400">
                • Click the connect button to sync contacts
              </span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 flex-shrink-0 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">Contacts</h1>
          <p className="text-white text-xs md:text-base">
            {filteredContacts.length > 0 ? (
              <>Showing {startIndex + 1}-{Math.min(endIndex, filteredContacts.length)} of {filteredContacts.length}</>
            ) : (
              <>No contacts yet</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
          {isAuthenticated && (
            <button 
              onClick={handleSync}
              disabled={syncing || authenticating}
              className="flex-1 sm:flex-initial bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-1 md:gap-2 transition-all text-xs md:text-base"
            >
              {syncing || authenticating ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  <span className="hidden sm:inline">{authenticating ? 'Connecting...' : 'Syncing...'}</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <Cloud size={16} />
                  <span className="hidden sm:inline">Refresh iCloud</span>
                  <span className="sm:hidden">Sync</span>
                </>
              )}
            </button>
          )}
          <button 
            className="flex-1 sm:flex-initial bg-white hover:bg-gray-200 text-black px-3 md:px-6 py-2 md:py-3 rounded-lg font-semibold flex items-center justify-center gap-1 md:gap-2 transition-all text-xs md:text-base"
            onClick={() => {
              setSelectedContact(null);
              setEditedContact({
                first_name: '',
                last_name: '',
                organization: '',
                job_title: '',
                emails: [{ email: '', type: 'personal' }],
                phones: [{ number: '', type: 'mobile' }],
                notes: ''
              });
              setIsEditMode(true);
              setIsDetailModalOpen(true);
            }}
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add Contact</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>
      
      {syncStatus && (
        <div className={`mb-3 md:mb-4 p-2 md:p-3 rounded-lg text-xs md:text-base ${syncStatus.includes('✅') ? 'bg-green-900/50 text-green-200' : syncStatus.includes('🔄') ? 'bg-blue-900/50 text-blue-200' : 'bg-red-900/50 text-red-200'}`}>
          {syncStatus}
        </div>
      )}

      {/* Main Container */}
      <div className="mb-4 bg-black border border-gray-400 rounded-xl p-2 md:p-3 flex-1 overflow-hidden flex flex-col hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 md:mb-4 gap-2">
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="flex items-center gap-1 md:gap-2">
              <Building2 size={18} className="text-white" />
              <span className="text-white font-semibold text-sm md:text-lg">All Contacts</span>
              <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-white bg-opacity-20 text-[10px] md:text-xs font-bold text-white">
                {filteredContacts.length}
              </span>
            </div>
            
            {/* Sort Filter */}
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-2 md:px-3 py-1 md:py-1.5 bg-gray-800 text-white border border-gray-600 rounded-lg text-[10px] md:text-xs focus:outline-none focus:border-blue-500"
            >
              <option value="a-z">A to Z</option>
              <option value="z-a">Z to A</option>
              <option value="newest">Newest</option>
            </select>
          </div>
          
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full sm:w-48 md:w-80 px-3 md:px-4 py-1.5 md:py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-xs md:text-sm"
          />
        </div>

        {/* Category Tabs - Compact for one line */}
        <div className="mb-3 md:mb-4 flex gap-1 md:gap-1.5 overflow-x-auto pb-2 items-center">
          {categories.map((category) => {
            // Count contacts for this category based on the active account's contacts only
            // Use getDisplayContacts() to get the filtered list for the current account
            const accountContacts = (() => {
              // Get contacts for the active account
              if (activeNylasAccount === 'icloud') {
                return categorizedContacts;
              } else if (activeNylasAccount === 'all') {
                let allContacts = [];
                if (icloudSynced) {
                  allContacts = [...categorizedContacts.map(c => ({ ...c, source: 'icloud' }))];
                }
                if (nylasAccounts.length > 0) {
                  allContacts = [...allContacts, ...nylasContacts];
                }
                return allContacts;
              } else if (nylasAccounts.length > 0) {
                return nylasContacts.filter(c => c.account_email === activeNylasAccount);
              }
              return categorizedContacts;
            })();
            
            const categoryCount = category.id === 'all' 
              ? accountContacts.length 
              : accountContacts.filter(c => c.category === category.id).length;
            
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                data-testid={`contact-category-${category.id}-btn`}
                className={`px-2 md:px-2.5 py-1 md:py-1.5 rounded-md text-[10px] md:text-xs font-semibold transition-all flex items-center gap-1 md:gap-1.5 whitespace-nowrap ${
                  selectedCategory === category.id
                    ? `${category.color} ${category.textColor} shadow-lg`
                    : 'bg-black border border-white text-white hover:bg-white/10'
                }`}
              >
                <span className="text-xs md:text-sm">{category.icon}</span>
                <span>{category.name}</span>
                {category.id !== 'all' && categoryCount > 0 && (
                  <span className="opacity-75 hidden sm:inline">
                    ({categoryCount})
                  </span>
                )}
              </button>
            );
          })}
          
          {/* Edit Categories Button */}
          <button
            onClick={() => setShowContactCategoryModal(true)}
            data-testid="edit-contact-categories-btn"
            className="px-2 py-1 md:py-1.5 rounded-md text-[10px] md:text-xs font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
            title="Edit Categories"
          >
            <Pencil size={12} />
            <span className="hidden md:inline">Edit</span>
          </button>
        </div>

        {/* Content Area */}
        <div className="bg-transparent rounded-lg flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 p-2 md:p-4">
            {paginatedContacts.map((contact, index) => {
              const category = categories.find(c => c.id === contact.category);
              // Handle both Nylas (given_name/surname) and iCloud (first_name/last_name) formats
              const displayName = contact.name || 
                `${contact.given_name || contact.first_name || ''} ${contact.surname || contact.last_name || ''}`.trim() || 
                'Unknown';
              const displayEmail = contact.email || (contact.emails && contact.emails[0]?.email) || '';
              const displayPhone = contact.phone || (contact.phones && contact.phones[0]?.number) || 
                (contact.phone_numbers && contact.phone_numbers[0]?.number) || '';
              const displayCompany = contact.company_name || contact.company || contact.organization || '';
              const displayNotes = contact.notes || '';
              const isIcloudContact = !!contact.icloud_id;
              const isNylasContact = contact.source === 'nylas';
              
              // Get initials for avatar (no default image)
              const initials = displayName
                .split(' ')
                .filter(n => n && n.length > 0)
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || '?';
              
              // Generate consistent color based on name
              const colors = [
                'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
                'bg-orange-600', 'bg-green-600', 'bg-teal-600', 
                'bg-cyan-600', 'bg-indigo-600'
              ];
              const colorIndex = (displayName && displayName.length > 0) ? displayName.charCodeAt(0) % colors.length : 0;
              // Use category color if available, otherwise fallback to name-based color
              const avatarColor = category ? category.color : colors[colorIndex];
              
              return (
                <div
                  key={contact.id || contact.icloud_id || `contact-${index}`}
                  onClick={() => {
                    setSelectedContact(contact);
                    setIsDetailModalOpen(true);
                  }}
                  className="bg-black border border-gray-400 rounded-xl p-5 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 cursor-pointer"
                  data-testid={`contact-card-${contact.id || index}`}
                >
                  {/* Avatar and Category Badge */}
                  <div className="flex flex-col items-center mb-4">
                    <div className={`w-20 h-20 rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-2xl border-2 border-white/20 mb-3 overflow-hidden`}>
                      {contact.profile_image || contact.picture_url ? (
                        <img src={contact.profile_image || contact.picture_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        initials
                      )}
                    </div>
                    
                    {/* Name */}
                    <h3 className="text-white font-semibold text-center text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
                      {displayName}
                    </h3>
                    
                    {/* Source Badge */}
                    {isNylasContact && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-600 text-white mb-3 flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        </svg>
                        {contact.account_email?.split('@')[0] || 'Nylas'}
                      </span>
                    )}
                    
                    {/* Category Badge with Edit Option */}
                    {!isNylasContact && (
                      <div className="relative mb-3">
                        {editingContactCardCategory === (contact.id || contact.icloud_id) ? (
                          // Inline Category Selector
                          <div 
                            className="absolute z-20 top-0 left-1/2 -translate-x-1/2 w-64 bg-gray-900 border border-gray-600 rounded-xl p-3 shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-gray-300">Select Category</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingContactCardCategory(null);
                                }}
                                className="p-1 hover:bg-gray-700 rounded"
                              >
                                <X size={14} className="text-gray-400" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
                              {categories.filter(c => c.id !== 'all').map((cat) => (
                                <button
                                  key={cat.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateContactCategoryAssignment(contact, cat.id);
                                  }}
                                  data-testid={`card-category-${cat.id}`}
                                  className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                                    contact.category === cat.id 
                                      ? `${cat.color} ${cat.textColor} ring-1 ring-white` 
                                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                  }`}
                                >
                                  <span>{cat.icon}</span>
                                  <span className="truncate">{cat.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // Category Badge with Edit Button
                          <div className="flex items-center justify-center gap-1 group">
                            <span 
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                category 
                                  ? `${category.color} ${category.textColor}` 
                                  : 'bg-gray-600 text-white'
                              }`}
                            >
                              {category ? `${category.icon} ${category.name}` : (isIcloudContact ? 'iCloud' : 'Other')}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingContactCardCategory(contact.id || contact.icloud_id);
                              }}
                              data-testid={`edit-category-btn-${contact.id || index}`}
                              className="p-1 rounded-full bg-gray-700/50 hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Change category"
                            >
                              <Pencil size={10} className="text-gray-300" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Contact Info */}
                  <div className="space-y-2 mb-4 min-h-[4rem]">
                    {displayEmail && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Mail size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayEmail}</span>
                      </div>
                    )}
                    {displayPhone && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Phone size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayPhone}</span>
                      </div>
                    )}
                    {displayCompany && (
                      <div className="flex items-center gap-2 text-xs text-gray-300">
                        <Briefcase size={12} className="flex-shrink-0" />
                        <span className="truncate">{displayCompany}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Notes Section */}
                  {displayNotes && displayNotes.trim() !== '' && (
                    <div className="mb-4 pt-3 border-t border-white/20">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-xs font-medium">Notes</span>
                      </div>
                      <p className="text-gray-400 text-xs line-clamp-2">{displayNotes}</p>
                    </div>
                  )}
                  
                  {/* Action Icons */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title={displayEmail ? `Email ${displayEmail}` : 'No email available'}
                      data-testid={`email-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        sendEmail(displayEmail);
                      }}
                    >
                      <Mail size={16} className={!displayEmail ? 'opacity-50' : ''} />
                    </button>
                    <button 
                      className="text-white hover:text-green-400 transition-colors p-1"
                      title={displayPhone ? `Call ${displayPhone}` : 'No phone available'}
                      data-testid={`call-btn-${contact.icloud_id || index}`}
                      disabled={callingContactId === (contact.icloud_id || contact.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        initiateCall(contact, displayPhone);
                      }}
                    >
                      {callingContactId === (contact.icloud_id || contact.id) ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Phone size={16} className={!displayPhone ? 'opacity-50' : ''} />
                      )}
                    </button>
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title="View Company"
                      data-testid={`company-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (contact.organization) {
                          window.open(`https://www.google.com/search?q=${encodeURIComponent(contact.organization)}`, '_blank');
                        }
                      }}
                    >
                      <Building2 size={16} className={!contact.organization ? 'opacity-50' : ''} />
                    </button>
                    <button 
                      className="text-white hover:text-yellow-400 transition-colors p-1"
                      title="Edit Contact"
                      data-testid={`edit-btn-${contact.icloud_id || index}`}
                      onClick={(e) => openEditModal(contact, e)}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="text-white hover:text-cyan-400 transition-colors p-1"
                      title="View Profile"
                      data-testid={`profile-btn-${contact.icloud_id || index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedContact(contact);
                        setIsDetailModalOpen(true);
                      }}
                    >
                      <Users size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-6 border-t border-gray-700">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
              >
                Next
              </button>
              
              <span className="text-gray-400 text-sm ml-4">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Contact Detail Modal */}
      {isDetailModalOpen && (selectedContact || isEditMode) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-8" onClick={() => { setIsDetailModalOpen(false); setIsEditMode(false); setEditedContact(null); setLinkedinImportedData(null); setLinkedinUrlInput(''); }}>
        <div className="bg-black border-2 border-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {isEditMode ? (selectedContact ? 'Edit Contact' : 'Add New Contact') : 'Contact Details'}
              </h2>
              <div className="flex items-center gap-3">
                {!isEditMode && selectedContact && (
                  <>
                    <button 
                      onClick={handleEditContact}
                      className="text-blue-400 hover:text-blue-300 px-4 py-2 border border-blue-400 rounded-lg transition-colors"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={handleDeleteContact}
                      className="text-red-400 hover:text-red-300 px-4 py-2 border border-red-400 rounded-lg transition-colors"
                    >
                      Delete
                    </button>
                  </>
                )}
                <button onClick={() => { setIsDetailModalOpen(false); setIsEditMode(false); setEditedContact(null); setLinkedinImportedData(null); setLinkedinUrlInput(''); }} className="text-gray-400 hover:text-white">
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contact Info - View Mode */}
            {!isEditMode && selectedContact && (
              <>
                <div className="space-y-6">
                  {/* Name & Avatar */}
                  <div className="flex items-center gap-4">
                    <div className={`w-20 h-20 rounded-full ${(() => {
                      // Use category color if available, otherwise fallback to name-based color
                      const cat = categories.find(c => c.id === selectedContact.category);
                      if (cat) return cat.color;
                      const name = `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim();
                      const colors = ['bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600', 'bg-orange-600', 'bg-green-600', 'bg-teal-600', 'bg-cyan-600', 'bg-indigo-600'];
                      return colors[name.charCodeAt(0) % colors.length];
                    })()} flex items-center justify-center text-white font-bold text-2xl border-2 border-white/20`}>
                      {selectedContact.profile_image ? (
                        <img src={selectedContact.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}</h3>
                      {selectedContact.organization && (
                        <p className="text-gray-400">{selectedContact.organization}</p>
                      )}
                    </div>
                  </div>

                  {/* Category Badge - Editable */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-semibold text-gray-400">Category</h4>
                      <button
                        onClick={() => setEditingContactCardCategory(
                          editingContactCardCategory === 'detail-modal' ? null : 'detail-modal'
                        )}
                        className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                        data-testid="edit-category-detail-btn"
                      >
                        <Pencil size={12} className="text-gray-400" />
                      </button>
                    </div>
                    
                    {editingContactCardCategory === 'detail-modal' ? (
                      // Category Selector Grid
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                        {categories.filter(c => c.id !== 'all').map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => {
                              updateContactCategoryAssignment(selectedContact, cat.id);
                            }}
                            data-testid={`detail-category-${cat.id}`}
                            className={`px-2 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
                              selectedContact.category === cat.id 
                                ? `${cat.color} ${cat.textColor} ring-2 ring-white` 
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            <span>{cat.icon}</span>
                            <span className="truncate">{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      // Display Current Category
                      (() => {
                        const cat = categories.find(c => c.id === selectedContact.category);
                        return cat ? (
                          <span className={`px-4 py-2 rounded-lg ${cat.color} ${cat.textColor} text-sm font-semibold inline-flex items-center gap-2`}>
                            <span>{cat.icon}</span>
                            <span>{cat.name}</span>
                          </span>
                        ) : (
                          <span className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 text-sm font-semibold">
                            No category assigned
                          </span>
                        );
                      })()
                    )}
                  </div>

                  {/* Emails */}
                  {selectedContact.emails && selectedContact.emails.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Email</h4>
                      {selectedContact.emails.map((email, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-white mb-2">
                          <Mail size={16} className="text-gray-400" />
                          <a href={`mailto:${email.email}`} className="hover:text-blue-400">
                            {email.email}
                          </a>
                          {email.type && <span className="text-xs text-gray-500">({email.type})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Phones */}
                  {selectedContact.phones && selectedContact.phones.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Phone</h4>
                      {selectedContact.phones.map((phone, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-white mb-2">
                          <Phone size={16} className="text-gray-400" />
                          <a href={`tel:${phone.number}`} className="hover:text-blue-400">
                            {phone.number}
                          </a>
                          {phone.type && <span className="text-xs text-gray-500">({phone.type})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Organization */}
                  {selectedContact.organization && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Organization</h4>
                      <p className="text-white">{selectedContact.organization}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedContact.notes && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 mb-2">Notes</h4>
                      <p className="text-white whitespace-pre-wrap">{selectedContact.notes}</p>
                    </div>
                  )}

                  {/* LinkedIn Profile Info (if available) */}
                  {(selectedContact.linkedin_url || selectedContact.job_title || selectedContact.location || selectedContact.education || selectedContact.company_website || selectedContact.about) && (
                    <div className="pt-4 border-t border-gray-700">
                      <h4 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2">
                        <Linkedin size={14} className="text-blue-500" />
                        LinkedIn Info
                      </h4>
                      {selectedContact.job_title && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <Briefcase size={16} className="text-gray-400" />
                          <span>{selectedContact.job_title}</span>
                        </div>
                      )}
                      {selectedContact.location && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <MapPin size={16} className="text-gray-400" />
                          <span>{selectedContact.location}</span>
                        </div>
                      )}
                      {selectedContact.education && (
                        <div className="flex items-center gap-2 text-white mb-2">
                          <GraduationCap size={16} className="text-indigo-400" />
                          <span>{selectedContact.education}</span>
                        </div>
                      )}
                      {selectedContact.company_website && (
                        <div className="mb-2">
                          <a 
                            href={selectedContact.company_website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-2 px-3 py-2 bg-teal-600/20 hover:bg-teal-600/30 border border-teal-500/50 rounded-lg text-teal-400 hover:text-teal-300 transition-colors"
                          >
                            <Globe size={16} className="text-teal-400" />
                            <span className="text-sm truncate flex-1">{selectedContact.company_website}</span>
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                      {selectedContact.about && (
                        <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
                          <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedContact.about}</p>
                        </div>
                      )}
                      {selectedContact.linkedin_url && (
                        <div className="mb-2 mt-3">
                          <a 
                            href={selectedContact.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/50 rounded-lg text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <Linkedin size={18} className="text-blue-500" />
                            <span className="text-sm truncate flex-1">{selectedContact.linkedin_url}</span>
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Import from LinkedIn Section */}
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <div className="p-4 bg-gray-800/50 border border-gray-600/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-4">
                        <Linkedin size={20} className="text-blue-500" />
                        <span className="text-white font-semibold">Import from LinkedIn</span>
                      </div>
                      
                      {/* LinkedIn URL Input */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1 relative">
                          <Link size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={linkedinUrlInput}
                            onChange={(e) => setLinkedinUrlInput(e.target.value)}
                            placeholder="Paste LinkedIn Profile URL"
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                          />
                        </div>
                        <button
                          onClick={() => handleLinkedInUrlScan(linkedinUrlInput)}
                          disabled={isScanning || !linkedinUrlInput}
                          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 whitespace-nowrap text-sm"
                        >
                          {isScanning ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Scanning...
                            </>
                          ) : (
                            <>
                              <Search size={14} />
                              Scan URL
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Auto Search Button */}
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">Don&apos;t have the URL?</span>
                        <button
                          onClick={handleAutoSearchLinkedIn}
                          disabled={isSearchingLinkedIn || isScanning}
                          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          {isSearchingLinkedIn ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Search size={14} />
                              Auto-Find Profile
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Found URL Message */}
                      {linkedinImportedData && linkedinImportedData.found && (
                        <div className="mt-3 p-3 bg-green-900/30 border border-green-500/50 rounded-lg">
                          <div className="flex items-center gap-2 text-green-300 text-sm">
                            <CheckCircle size={16} />
                            <span>{linkedinImportedData.message}</span>
                          </div>
                          {linkedinImportedData.url && (
                            <a href={linkedinImportedData.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mt-1">
                              {linkedinImportedData.url}
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      )}

                      {/* LinkedIn Import Success */}
                      {linkedinImportedData && !linkedinImportedData.error && !linkedinImportedData.found && linkedinImportedData.fieldCount > 0 && (
                        <div className="mt-3 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle size={16} className="text-blue-400" />
                            <span className="text-blue-300 font-semibold text-sm">✅ LinkedIn Data Found! ({linkedinImportedData.fieldCount} fields)</span>
                          </div>
                          <div className="space-y-1 text-xs">
                            {linkedinImportedData.fields.first_name && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Name:</span> {linkedinImportedData.fields.first_name} {linkedinImportedData.fields.last_name || ''}
                              </div>
                            )}
                            {linkedinImportedData.fields.job_title && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Title:</span> {linkedinImportedData.fields.job_title}
                              </div>
                            )}
                            {linkedinImportedData.fields.organization && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Company:</span> {linkedinImportedData.fields.organization}
                              </div>
                            )}
                            {linkedinImportedData.fields.location && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">Location:</span> {linkedinImportedData.fields.location}
                              </div>
                            )}
                            {linkedinImportedData.fields.linkedin_url && (
                              <div className="text-gray-300">
                                <span className="text-gray-500">URL:</span> <a href={linkedinImportedData.fields.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400">{linkedinImportedData.fields.linkedin_url}</a>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setIsEditMode(true);
                              // Data is already in editedContact from the scan
                            }}
                            className="mt-3 w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <CheckCircle size={14} />
                            Save to Contact
                          </button>
                        </div>
                      )}

                      {/* Error State */}
                      {linkedinImportedData && linkedinImportedData.error && (
                        <div className="mt-3 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                          <p className="text-red-300 text-sm">{linkedinImportedData.message}</p>
                          {linkedinImportedData.searchedName && (
                            <p className="text-gray-400 text-xs mt-1">Searched: {linkedinImportedData.searchedName}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-700">
                  <button
                    onClick={() => selectedContact.emails?.[0] && (window.location.href = `mailto:${selectedContact.emails[0].email}`)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <Mail size={18} />
                    Email
                  </button>
                  <button
                    onClick={() => selectedContact.phones?.[0] && (window.location.href = `tel:${selectedContact.phones[0].number}`)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone size={18} />
                    Call
                  </button>
                </div>
              </>
            )}

            {/* Contact Info - Edit Mode */}
            {isEditMode && editedContact && (
              <>
                {/* LinkedIn Import Summary - Shows in edit mode after import */}
                {linkedinImportedData && !linkedinImportedData.error && !linkedinImportedData.found && linkedinImportedData.fieldCount > 0 && (
                  <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Linkedin size={18} className="text-blue-400" />
                      <span className="text-blue-300 font-semibold text-sm">✅ LinkedIn data applied to form below</span>
                    </div>
                    <p className="text-blue-300 text-xs">Review the fields and click Save Changes to update contact.</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Name Fields */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Name</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editedContact.first_name || ''}
                        onChange={(e) => updateEditedField('first_name', e.target.value)}
                        placeholder="First Name"
                        className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      <input
                        type="text"
                        value={editedContact.last_name || ''}
                        onChange={(e) => updateEditedField('last_name', e.target.value)}
                        placeholder="Last Name"
                        className="px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Category Selector */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Category</h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {categories.filter(c => c.id !== 'all').map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => updateEditedField('category', cat.id)}
                          data-testid={`category-select-${cat.id}`}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                            editedContact.category === cat.id 
                              ? `${cat.color} ${cat.textColor} ring-2 ring-white ring-offset-2 ring-offset-black` 
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                          }`}
                        >
                          <span>{cat.icon}</span>
                          <span className="truncate">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Organization */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Organization</h4>
                    <input
                      type="text"
                      value={editedContact.organization || ''}
                      onChange={(e) => updateEditedField('organization', e.target.value)}
                      placeholder="Company/Organization"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Job Title */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Job Title</h4>
                    <input
                      type="text"
                      value={editedContact.job_title || ''}
                      onChange={(e) => updateEditedField('job_title', e.target.value)}
                      placeholder="Job Title / Position"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* LinkedIn Profile URL */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <Linkedin size={14} className="text-blue-500" />
                      LinkedIn Profile URL
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editedContact.linkedin_url || ''}
                        onChange={(e) => updateEditedField('linkedin_url', e.target.value)}
                        placeholder="https://linkedin.com/in/username"
                        className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      {editedContact.linkedin_url && (
                        <a
                          href={editedContact.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <MapPin size={14} className="text-gray-400" />
                      Location
                    </h4>
                    <input
                      type="text"
                      value={editedContact.location || ''}
                      onChange={(e) => updateEditedField('location', e.target.value)}
                      placeholder="City, State/Country"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Education */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <GraduationCap size={14} className="text-indigo-400" />
                      Education
                    </h4>
                    <input
                      type="text"
                      value={editedContact.education || ''}
                      onChange={(e) => updateEditedField('education', e.target.value)}
                      placeholder="University, Degree (e.g., Harvard - MBA)"
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {/* Company Website */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                      <Globe size={14} className="text-teal-400" />
                      Company Website
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={editedContact.company_website || ''}
                        onChange={(e) => updateEditedField('company_website', e.target.value)}
                        placeholder="https://company.com"
                        className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                      {editedContact.company_website && (
                        <a
                          href={editedContact.company_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-1"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* About / Bio */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">About / Bio</h4>
                    <textarea
                      value={editedContact.about || ''}
                      onChange={(e) => updateEditedField('about', e.target.value)}
                      placeholder="Professional summary or bio from LinkedIn..."
                      rows={3}
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>

                  {/* Emails */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-400">Email Addresses</h4>
                      <button
                        onClick={addEmail}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Email
                      </button>
                    </div>
                    {editedContact.emails?.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <input
                          type="email"
                          value={email.email || ''}
                          onChange={(e) => updateEditedEmail(idx, e.target.value)}
                          placeholder="email@example.com"
                          className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => removeEmail(idx)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Phones */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-gray-400">Phone Numbers</h4>
                      <button
                        onClick={addPhone}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Plus size={14} />
                        Add Phone
                      </button>
                    </div>
                    {editedContact.phones?.map((phone, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <input
                          type="tel"
                          value={phone.number || ''}
                          onChange={(e) => updateEditedPhone(idx, e.target.value)}
                          placeholder="+1 234 567 8900"
                          className="flex-1 px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={() => removePhone(idx)}
                          className="text-red-400 hover:text-red-300 p-2"
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-400 mb-2">Notes</h4>
                    <textarea
                      value={editedContact.notes || ''}
                      onChange={(e) => updateEditedField('notes', e.target.value)}
                      placeholder="Add notes about this contact..."
                      rows={4}
                      className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                </div>

                {/* Edit Actions */}
                <div className="flex gap-3 mt-6 pt-6 border-t border-gray-700">
                  <button
                    onClick={handleCancelEdit}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContact}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
                  >
                    Save Changes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}

    {/* iCloud Credentials Modal */}
    {showCredentialsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect iCloud</h3>
                <p className="text-sm text-gray-400">Sign in to import your contacts</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            if (icloudEmail && icloudAppPassword) {
              authenticateAndSync(icloudEmail, icloudAppPassword);
            }
          }} className="p-6 space-y-4">
            {credentialError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {credentialError}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Apple ID (Email)</label>
              <input
                type="email"
                value={icloudEmail}
                onChange={(e) => setIcloudEmail(e.target.value)}
                placeholder="your@icloud.com"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                required
                data-testid="icloud-email-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">App-Specific Password</label>
              <input
                type="password"
                value={icloudAppPassword}
                onChange={(e) => setIcloudAppPassword(e.target.value)}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                required
                data-testid="icloud-password-input"
              />
              <p className="mt-2 text-xs text-gray-500">
                Generate an app-specific password at{' '}
                <a 
                  href="https://appleid.apple.com/account/manage" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  appleid.apple.com
                </a>
                {' '}→ Sign-In & Security → App-Specific Passwords
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCredentialsModal(false);
                  setCredentialError('');
                }}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={authenticating || !icloudEmail || !icloudAppPassword}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="icloud-connect-submit"
              >
                {authenticating ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Cloud size={18} />
                    Connect
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Google Contacts Connection Modal */}
    {showGoogleContactsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGoogleContactsModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white rounded-lg">
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect Google Contacts</h3>
                <p className="text-sm text-gray-400">Import your Google contacts</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                  <p>Click "Connect with Google" below</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                  <p>Sign in to your Google account</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                  <p>Allow access to your contacts</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                  <p>Your contacts will be imported automatically</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> Google Contacts uses OAuth for secure authentication. Your credentials are never stored on our servers.
              </p>
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => setShowGoogleContactsModal(false)}
              className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                window.open('https://contacts.google.com/', '_blank');
                setShowGoogleContactsModal(false);
              }}
              className="flex-1 px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connect with Google
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Outlook Contacts Connection Modal */}
    {showOutlookContactsModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOutlookContactsModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#0078D4] rounded-lg">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Connect Outlook Contacts</h3>
                <p className="text-sm text-gray-400">Import your Outlook/Microsoft contacts</p>
              </div>
            </div>
          </div>
          
          <div className="p-6 space-y-4">
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-3">How to Connect:</h4>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">1</span>
                  <p>Click "Connect with Outlook" below</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">2</span>
                  <p>Sign in to your Microsoft account</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">3</span>
                  <p>Allow access to your contacts</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#0078D4] rounded-full flex items-center justify-center text-white text-xs font-bold">4</span>
                  <p>Your contacts will be synced to your dashboard</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
              <h4 className="text-white font-semibold mb-2">Or Sync from Desktop:</h4>
              <p className="text-sm text-gray-400">
                You can also sync contacts from Outlook Desktop on your Mac using the Shortcuts automation app.
              </p>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-xs text-blue-400">
                <strong>Note:</strong> Outlook uses OAuth for secure authentication. Your credentials are never stored on our servers.
              </p>
            </div>
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => setShowOutlookContactsModal(false)}
              className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                window.open('https://outlook.live.com/people/', '_blank');
                setShowOutlookContactsModal(false);
              }}
              className="flex-1 px-4 py-3 bg-[#0078D4] hover:bg-[#106EBE] text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229c.102.086.221.127.358.127.142 0 .262-.043.363-.127l6.81-5.25c.088-.064.153-.14.193-.23.043-.088.039-.168-.008-.238v-.012c-.03-.058-.072-.11-.128-.155-.055-.047-.12-.074-.19-.086h-.012c-.063-.013-.122-.013-.175 0-.053.012-.104.035-.153.067l-6.81 5.25-1.848-1.42v-3.48h8.547c.226 0 .42.076.578.23.158.152.238.346.238.576z"/>
              </svg>
              Connect with Outlook
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Contact Account Modal */}
    {showAddContactAccountModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddContactAccountModal(false)}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-xl font-bold text-white mb-1">Add Contact Account</h3>
            <p className="text-sm text-gray-400">Connect a new contact source to your dashboard</p>
          </div>
          
          <div className="p-6 space-y-4">
            {addContactAccountError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {addContactAccountError}
              </div>
            )}
            
            {/* Tab Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tab Name</label>
              <input
                type="text"
                value={newContactAccountName}
                onChange={(e) => setNewContactAccountName(e.target.value)}
                placeholder="e.g., Work, Personal, Family"
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                data-testid="new-contact-account-name"
              />
              <p className="mt-1 text-xs text-gray-500">This name will appear as the tab label</p>
            </div>
            
            {/* Contact Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Contact Provider</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('icloud')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'icloud'
                      ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="text-xs font-medium">iCloud</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('google')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'google'
                      ? 'bg-red-600/20 border-red-500 text-red-400'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  </svg>
                  <span className="text-xs font-medium">Google</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewContactAccountType('outlook')}
                  className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                    newContactAccountType === 'outlook'
                      ? 'bg-blue-600/20 border-[#0078D4] text-[#0078D4]'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229z"/>
                  </svg>
                  <span className="text-xs font-medium">Outlook</span>
                </button>
              </div>
            </div>
            
            {/* Credentials Section - Show based on provider type */}
            {newContactAccountType === 'icloud' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  iCloud Credentials
                </h4>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Apple ID (Email)</label>
                  <input
                    type="email"
                    value={newContactAccountEmail}
                    onChange={(e) => setNewContactAccountEmail(e.target.value)}
                    placeholder="your@icloud.com"
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">App-Specific Password</label>
                  <input
                    type="password"
                    value={newContactAccountPassword}
                    onChange={(e) => setNewContactAccountPassword(e.target.value)}
                    placeholder="xxxx-xxxx-xxxx-xxxx"
                    className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Generate at{' '}
                    <a 
                      href="https://appleid.apple.com/account/manage" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      appleid.apple.com
                    </a>
                  </p>
                </div>
              </div>
            )}
            
            {newContactAccountType === 'google' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#EA4335">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  </svg>
                  Google Contacts
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Google uses OAuth for secure authentication. After adding, click the connect button to sign in with Google.
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                  <p className="text-xs text-green-400">
                    ✓ No password needed - Uses secure Google sign-in
                  </p>
                </div>
              </div>
            )}
            
            {newContactAccountType === 'outlook' && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                <h4 className="text-white font-semibold flex items-center gap-2 mb-2">
                  <svg className="w-4 h-4 text-[#0078D4]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.154-.352.23-.578.23h-8.547v-6.959l1.6 1.229z"/>
                  </svg>
                  Outlook Contacts
                </h4>
                <p className="text-sm text-gray-400 mb-3">
                  Outlook uses OAuth for secure authentication. After adding, click the connect button to sign in with Microsoft.
                </p>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2">
                  <p className="text-xs text-green-400">
                    ✓ No password needed - Uses secure Microsoft sign-in
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={() => {
                setShowAddContactAccountModal(false);
                setNewContactAccountName('');
                setNewContactAccountEmail('');
                setNewContactAccountType('icloud');
                setNewContactAccountPassword('');
                setAddContactAccountError('');
              }}
              className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddContactAccount}
              className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="add-contact-account-submit"
            >
              <Plus size={18} />
              Add Account
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Contact Category Management Modal */}
    {showContactCategoryModal && (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={() => setShowContactCategoryModal(false)}
      >
        <div 
          className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Edit Contact Categories</h3>
            <button
              onClick={() => setShowContactCategoryModal(false)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={18} className="text-gray-400" />
            </button>
          </div>
          
          {/* Add New Category */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex gap-2">
              {/* Icon Selector */}
              <div className="relative">
                <button
                  type="button"
                  className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center hover:border-gray-500"
                  onClick={(e) => {
                    const dropdown = e.currentTarget.nextElementSibling;
                    dropdown.classList.toggle('hidden');
                  }}
                >
                  {newContactCategoryIcon}
                </button>
                <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-6 gap-1 w-48">
                  {categoryIcons.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={(e) => {
                        setNewContactCategoryIcon(icon);
                        e.currentTarget.parentElement.classList.add('hidden');
                      }}
                      className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                value={newContactCategoryName}
                onChange={(e) => setNewContactCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addContactCategory()}
                placeholder="New category name..."
                className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none"
                data-testid="new-contact-category-input"
              />
              <button
                onClick={addContactCategory}
                disabled={!newContactCategoryName.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-1.5"
                data-testid="add-contact-category-btn"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
          </div>
          
          {/* Category List */}
          <div className="p-4 max-h-80 overflow-y-auto space-y-2">
            {categories.filter(c => c.id !== 'all').map((category) => {
              const protectedCategories = ['passwords', 'real-estate', 'family', 'other'];
              const isProtected = protectedCategories.includes(category.id);
              
              return (
                <div 
                  key={category.id}
                  className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group"
                >
                  {editingContactCategory === category.id ? (
                    <>
                      <span className="text-lg">{category.icon}</span>
                      <input
                        type="text"
                        value={editContactCategoryValue}
                        onChange={(e) => setEditContactCategoryValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') updateContactCategory(category.id, editContactCategoryValue, category.icon);
                          if (e.key === 'Escape') {
                            setEditingContactCategory(null);
                            setEditContactCategoryValue('');
                          }
                        }}
                        className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm focus:border-purple-500 focus:outline-none"
                        autoFocus
                        data-testid={`edit-contact-category-input-${category.id}`}
                      />
                      <button
                        onClick={() => updateContactCategory(category.id, editContactCategoryValue, category.icon)}
                        className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"
                        title="Save"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingContactCategory(null);
                          setEditContactCategoryValue('');
                        }}
                        className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center text-sm`}>
                        {category.icon}
                      </span>
                      <span className="flex-1 text-white text-sm">
                        {category.name}
                        {isProtected && (
                          <span className="ml-2 text-xs text-gray-500">(default)</span>
                        )}
                      </span>
                      <button
                        onClick={() => {
                          setEditingContactCategory(category.id);
                          setEditContactCategoryValue(category.name);
                        }}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit"
                        data-testid={`edit-contact-category-btn-${category.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      {!isProtected && (
                        <button
                          onClick={() => deleteContactCategory(category.id)}
                          className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete"
                          data-testid={`delete-contact-category-btn-${category.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Modal Footer */}
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={() => setShowContactCategoryModal(false)}
              className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
              data-testid="close-contact-category-modal-btn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default RealEstateManagingPartnerContacts;
